import { InjectionToken, Injector, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { LayoutModule } from '@angular/cdk/layout';
import { QuillModule } from 'ngx-quill';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QuillComponent } from './components/quill/quill.component';
import { provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '@dash/randy/shared/components/confirm-dialog/confirm-dialog.component';
import { StarRatingComponent } from '@dash/randy/shared/components/star-rating/star-rating.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTreeModule } from '@angular/material/tree';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { FlexLayoutModule } from '@angular/flex-layout';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SmallStatusComponent } from './components/small-status/small-status.component';
import { FileManagerItemIconComponent } from './components/file-manager-item-icon/file-manager-item-icon.component';
import { ImgFileEditorComponent } from './components/img-file-editor/img-file-editor.component';
import { MatTableHeaderToolbarComponent } from './components/mat-table-header-toolbar/mat-table-header-toolbar.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DragAndDropDirective } from './directive/drag-and-drop.directive';
import { SafePipe } from './pipes/safe.pipe';
import { MatBadgeModule } from '@angular/material/badge';
import { JoyrideModule } from 'ngx-joyride';
import { BaseComponent } from '@dash/randy/shared/classes/baseComponent';
import { DraggableDirective } from './directive/draggable.directive';
import { GlobalPageHeaderComponent } from './components/global-page-header/global-page-header.component';
import { VideoDialogComponent } from './components/video-dialog/video-dialog.component';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { NgModelChangeDebouncedDirective } from './directive/ng-model-change-debounced.directive';
import { HideProdDirective } from './directive/hide-prod.directive';
import { ConfirmDialogDirective } from './directive/confirm-dialog.directive';
import { ButtonSpinnerComponent } from './components/button-spinner/button-spinner.component';
import { DragAndDropOverlayComponent } from './components/drag-and-drop-overlay/drag-and-drop-overlay.component';
import { StorageSizePipe } from './pipes/storage-size.pipe';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { AdminUserEmailPipe } from '@dash/randy/shared/pipes/admin-user-email.pipe';

import { SimpleMatCardComponent } from './components/simple-mat-card/simple-mat-card.component';
import { MatTabsModule } from '@angular/material/tabs';
import { WithLoadingPipe } from '@dash/randy/shared/pipes/with-loading.pipe';
import { ImgCropperComponent } from './components/img-cropper/img-cropper.component';
import { IfDebugDirective } from './directive/if-debug.directive';
import { MtxTooltipModule } from '@ng-matero/extensions/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AuthPermissionCheckDirective } from './directive/auth-permission-check.directive';
import { AuthPermissionCheckInputDirective } from '@dash/randy/shared/directive/auth-permission-check-input.directive';
import { ApplicationFromIdPipe } from './pipes/application-from-id.pipe';
import { AppInjector } from '@dash/randy/shared/service/app.injector';
import { MatStepperModule } from '@angular/material/stepper';
import { CollectionItemDataDisplayPipe } from './pipes/collection-item-data-display.pipe';
import { DomSanitizer } from '@angular/platform-browser';
import { ErrorPageComponent } from './components/error-page/error-page.component';
import { BasePageComponent } from '@dash/randy/shared/components/base-page/base-page.component';
import { RouterModule } from '@angular/router';
import { SelectObjectTypeComponent } from './components/select-object-type/select-object-type.component';
import { LocaleSimplePipe } from '@dash/randy/shared/pipes/locale-simple.pipe';
import { MouseOverDirective } from '@dash/randy/shared/directive/mouse-over.directive';
import { AddHistoryRecordDirective } from './directive/add-history-record.directive';
import { GoToHistoryRecordDirective } from '@dash/randy/shared/directive/go-to-history-record.directive';
import { HideOnMobileDirective } from '@dash/randy/shared/directive/hide-on-mobile.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { VideoIDDirective } from './directive/video-id.directive';
import { ResizableColumnsDirective } from '@dash/randy/shared/directive/resizable-columns.directive';
import { NumbersOnlyDirective } from './directive/numbers-only.directive';
import { TruncatePipe } from '@dash/randy/shared/pipes/truncate.pipe';
import { AutofocusDirective } from '@dash/randy/shared/directive/autofocus.directive';
import { CustomSnackbarComponent } from '@dash/randy/shared/components/snackbar-components/custom-snackbar/custom-snackbar.component';
import { LoadingSnackbarComponent } from '@dash/randy/shared/components/snackbar-components/loading-snackbar/loading-snackbar.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { LoadingImageComponent } from '@dash/randy/shared/components/loading-img/loading-image.component';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { BytesPipe } from '@dash/randy/shared/pipes/bytes.pipe';
import { VideoCdnLinkPipe } from '@dash/randy/shared/components/file-manager-item-icon/video-cdn-link.pipe';
import { AudioCdnLinkPipe } from '@dash/randy/shared/components/file-manager-item-icon/audio-cdn-link.pipe';
import { VideoViewerComponent } from './components/video-viewer/video-viewer.component';
import { AccessControlTypeDirective } from '@dash/randy/shared/directive/access-control-type.directive';
import { PersistPaginatorDirective } from '@dash/randy/shared/directive/persist-paginator.directive';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SplitStorageDirective } from '@dash/randy/shared/directive/split-storage.directive';
import { ImgCdnLinkPipe } from '../shared-new/pipes/img-cdn-link.pipe';
import { CollectionItemFieldPipe } from '@dash/randy/shared-new/pipes/collection-item-field.pipe';
import { RandyTabRouterLinkDirective } from '@dash/randy/shared/directive/randy-tab-router-link.directive';

@NgModule({
  declarations: [
    QuillComponent,
    ConfirmDialogComponent,
    StarRatingComponent,
    SpinnerComponent,
    SmallStatusComponent,
    BaseComponent,
    FileManagerItemIconComponent,
    ImgFileEditorComponent,
    MatTableHeaderToolbarComponent,
    DragAndDropDirective,
    SafePipe,
    DraggableDirective,
    GlobalPageHeaderComponent,
    VideoDialogComponent,
    DateAgoPipe,
    NgModelChangeDebouncedDirective,
    HideProdDirective,
    ConfirmDialogDirective,
    ButtonSpinnerComponent,
    DragAndDropOverlayComponent,
    StorageSizePipe,
    AdminUserEmailPipe,
    ApplicationFromIdPipe,
    SimpleMatCardComponent,
    LocaleSimplePipe,
    TruncatePipe,
    BytesPipe,
    AuthPermissionCheckDirective,
    AuthPermissionCheckInputDirective,
    WithLoadingPipe,
    ImgCropperComponent,
    MouseOverDirective,
    CollectionItemDataDisplayPipe,
    ErrorPageComponent,
    BasePageComponent,
    SelectObjectTypeComponent,
    AddHistoryRecordDirective,
    GoToHistoryRecordDirective,
    HideOnMobileDirective,
    VideoIDDirective,
    ResizableColumnsDirective,
    NumbersOnlyDirective,
    BackButtonComponent,
    VideoCdnLinkPipe,
    AudioCdnLinkPipe,
    VideoViewerComponent,
    AccessControlTypeDirective,
    PersistPaginatorDirective,
    SplitStorageDirective,
  ],
  exports: [
    CommonModule,
    MatGridListModule,
    MatIconModule,
    MatToolbarModule,
    MatSidenavModule,
    MatPaginatorModule,
    MatSortModule,
    MatBadgeModule,
    MatBottomSheetModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    LayoutModule,
    QuillModule,
    QuillComponent,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatTreeModule,
    MatExpansionModule,
    FlexLayoutModule,
    DragDropModule,
    NgxMatSelectSearchModule,
    NgxSpinnerModule,
    JoyrideModule,
    MatStepperModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ConfirmDialogComponent,
    StarRatingComponent,
    NgxDropzoneModule,
    SpinnerComponent,
    SmallStatusComponent,
    BaseComponent,
    FileManagerItemIconComponent,
    MatTableHeaderToolbarComponent,
    DragAndDropDirective,
    ImgCdnLinkPipe,
    SafePipe,
    DraggableDirective,
    ResizableColumnsDirective,
    GlobalPageHeaderComponent,
    DateAgoPipe,
    NgModelChangeDebouncedDirective,
    HideProdDirective,
    ConfirmDialogDirective,
    ButtonSpinnerComponent,
    DragAndDropOverlayComponent,
    StorageSizePipe,
    AdminUserEmailPipe,
    ApplicationFromIdPipe,
    SimpleMatCardComponent,
    WithLoadingPipe,
    AuthPermissionCheckDirective,
    AuthPermissionCheckInputDirective,
    MouseOverDirective,
    MtxTooltipModule,
    CollectionItemDataDisplayPipe,
    ImgCdnLinkPipe,
    ErrorPageComponent,
    BasePageComponent,
    SelectObjectTypeComponent,
    AddHistoryRecordDirective,
    HideOnMobileDirective,
    VideoIDDirective,
    NumbersOnlyDirective,
    TruncatePipe,
    AutofocusDirective,
    LoadingImageComponent,
    BackButtonComponent,
    BytesPipe,
    VideoCdnLinkPipe,
    AudioCdnLinkPipe,
    AccessControlTypeDirective,
    PersistPaginatorDirective,
    SplitStorageDirective,
    RandyTabRouterLinkDirective,
    CollectionItemFieldPipe,
    CustomSnackbarComponent,
    LoadingSnackbarComponent,
  ],
  imports: [
    CommonModule,
    MatGridListModule,
    MatCardModule,
    MatMenuModule,
    MatIconModule,
    MatToolbarModule,
    MatSidenavModule,
    MatBottomSheetModule,
    MatListModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatRadioModule,
    LayoutModule,
    QuillModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatTreeModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FlexLayoutModule,
    DragDropModule,
    MatSlideToggleModule,
    NgxDropzoneModule,
    NgxSpinnerModule,
    MatBadgeModule,
    JoyrideModule.forChild(),
    MatTabsModule,
    RouterModule,
    NgxMatSelectSearchModule,
    MatButtonModule,
    MatFormFieldModule,
    ImgCdnLinkPipe,
    CollectionItemFieldPipe,
    LoadingImageComponent,
    CustomSnackbarComponent,
    LoadingSnackbarComponent,
    AutofocusDirective,
    RandyTabRouterLinkDirective,
  ],
  providers: [
    ImgCdnLinkPipe,
    MatDatepickerModule,
    BytesPipe,
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true, // this enables pass through for tooltips
      },
    },
    provideHttpClient(withJsonpSupport(), withInterceptorsFromDi()),
  ],
})
export class SharedModule {
  constructor(injector: Injector) {
    AppInjector.setInjector(injector);
    //http://localhost:4200/assets/custom_material_icons/columns_edit.svg
  }
}
